'use client';

import { useEffect, useRef, useState } from 'react';
import { Search, X } from 'lucide-react';

interface Props {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  resultCount?: number;
  debounceMs?: number;
}

export function SearchBar({
  value,
  onChange,
  placeholder = 'Search questions, tags, topics...',
  resultCount,
  debounceMs = 200,
}: Props) {
  const [localValue, setLocalValue] = useState(value);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    setLocalValue(value);
  }, [value]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    setLocalValue(newValue);

    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => {
      onChange(newValue);
    }, debounceMs);
  };

  const handleClear = () => {
    setLocalValue('');
    onChange('');
    if (timerRef.current) clearTimeout(timerRef.current);
  };

  return (
    <div className="relative w-full">
      <div className="absolute left-4 top-1/2 -translate-y-1/2 pointer-events-none">
        <Search size={18} style={{ color: 'var(--color-text-muted)' }} />
      </div>

      <input
        type="search"
        value={localValue}
        onChange={handleChange}
        placeholder={placeholder}
        className="w-full rounded-xl pl-11 pr-10 py-3 text-sm focus:outline-none transition-all"
        style={{
          background: 'var(--color-surface)',
          border: '1px solid var(--color-border)',
          color: 'var(--color-text-primary)',
        }}
        onFocus={(e) => {
          e.currentTarget.style.borderColor = 'var(--color-border-hover)';
        }}
        onBlur={(e) => {
          e.currentTarget.style.borderColor = 'var(--color-border)';
        }}
        aria-label="Search questions"
        role="searchbox"
      />

      {localValue && (
        <button
          onClick={handleClear}
          className="absolute right-3 top-1/2 -translate-y-1/2 p-1 rounded transition-colors"
          style={{ color: 'var(--color-text-muted)' }}
          aria-label="Clear search"
        >
          <X size={14} />
        </button>
      )}

      {resultCount !== undefined && localValue && (
        <div
          className="absolute right-10 top-1/2 -translate-y-1/2 text-xs pointer-events-none"
          style={{ color: 'var(--color-text-muted)' }}
        >
          {resultCount} result{resultCount !== 1 ? 's' : ''}
        </div>
      )}
    </div>
  );
}
